"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { getAccessToken } from "@/lib/auth/client";
import type { ProfilePanelResponse, ProfilePanelData } from "@/lib/api/types";

type MyProfile = ProfilePanelResponse["myProfile"];

interface ProfileContextValue {
  myProfile: MyProfile | null;
  membershipData: ProfilePanelData[];
  /** True when the viewer is the primary member of their first membership. */
  isPrimaryMember: boolean;
  /** Member IDs the viewer is allowed to edit (from first membership). */
  editableMemberIds: string[];
  isLoaded: boolean;
  /** True while a refresh fetch is in flight. */
  isRefreshing: boolean;
  /** Refresh profile data from the API. */
  refresh: () => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/**
 * @component ProfileProvider
 * @description Fetches /api/profile/members once at the dashboard layout level
 * and exposes the logged-in user's profile, membership data, and permission flags
 * to all descendants.
 */
export function ProfileProvider({ children }: { children: ReactNode }) {
  const [myProfile, setMyProfile] = useState<MyProfile | null>(null);
  const [membershipData, setMembershipData] = useState<ProfilePanelData[]>([]);
  const [isLoaded, setIsLoaded] = useState(() => !getAccessToken());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    let cancelled = false;

    fetch("/api/profile/members", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled) return;
        if (json?.success && json?.data) {
          setMyProfile(json.data.myProfile ?? null);
          setMembershipData(
            Array.isArray(json.data.membershipData) ? json.data.membershipData : [],
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setIsLoaded(true);
          setIsRefreshing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = () => {
    setIsRefreshing(true);
    setTick((t) => t + 1);
  };

  // Re-fetch when the browser tab becomes visible again so dropped members
  // disappear without requiring a full page reload.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const isPrimaryMember = membershipData.some((p) => p.isPrimaryMember);
  const editableMemberIds = [...new Set(membershipData.flatMap((p) => p.editableMemberIds))];

  return (
    <ProfileContext.Provider
      value={{
        myProfile,
        membershipData,
        isPrimaryMember,
        editableMemberIds,
        isLoaded,
        isRefreshing,
        refresh,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

/**
 * @hook useProfileData
 * @description Returns the logged-in user's profile and membership permission flags.
 * Must be used within a ProfileProvider.
 * @throws If called outside a ProfileProvider.
 */
export function useProfileData(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (ctx === null) {
    throw new Error("useProfileData must be used within a ProfileProvider");
  }
  return ctx;
}
